import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import MuiDialogActions from '@mui/material/DialogActions';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';

export interface IProps extends Omit<DialogProps, 'title'> {
    title?: React.ReactNode;
    actions?: React.ReactNode;
    children?: React.ReactNode;
}

const DialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
    borderBottom: `1px solid ${theme.vars.palette.grey[300]}`,
}));

const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
    '.MuiDialogTitle-root + &': { paddingTop: theme.spacing(2) },
}));

const DialogActions = styled(MuiDialogActions)(({ theme }) => ({
    justifyContent: 'flex-start',
    padding: theme.spacing(0, 3, 3, 3),
}));

export const AlertDialog = ({
    children,
    actions,
    onClose,
    title,
    ...other
}: IProps) => {
    const [translate] = useTranslation();

    const renderTitle = () => {
        if (!title) return null;

        if (!onClose) return <DialogTitle>{title}</DialogTitle>;

        return (
            <DialogTitle>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    {title}

                    <IconButton
                        aria-label={translate('close')}
                        edge="end"
                        onClick={(e) => onClose(e, 'escapeKeyDown')}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
        );
    };

    return (
        <Dialog {...other} onClose={onClose}>
            {renderTitle()}
            {children && <DialogContent>{children}</DialogContent>}
            {actions && <DialogActions>{actions}</DialogActions>}
        </Dialog>
    );
};
